import React, { useCallback, useMemo, useRef } from 'react';
import { CircularProgress, List } from '@material-ui/core';
import { Autocomplete, AutocompleteProps, FilterOptionsState } from '@material-ui/lab';
import InfiniteLoader from 'react-window-infinite-loader';
import { FixedSizeList, ListOnScrollProps, ListChildComponentProps } from 'react-window';
import { IAutoCompleteItem } from '../typings/autoComplete';
import { matchSorter } from 'match-sorter';

let scrollOffset = 0;

// ---------------------------------------------------------------------------
// Stable listbox factory
// ---------------------------------------------------------------------------
// Receives refs instead of values so the returned component type is stable —
// created once via useMemo and never recreated, which prevents React from
// unmounting the listbox mid-session and resetting the scroll position.

interface WithInfiniteProps {
    hasNextPageRef: React.MutableRefObject<boolean>;
    isNextPageLoadingRef: React.MutableRefObject<boolean>;
    loadNextPage: () => void;
    itemSizeRef: React.MutableRefObject<number>;
    heightRef: React.MutableRefObject<number | undefined>;
    reverseRenderRef: React.MutableRefObject<boolean | undefined>;
}

const withInfinite = ({
    hasNextPageRef,
    isNextPageLoadingRef,
    loadNextPage,
    itemSizeRef,
    heightRef,
    reverseRenderRef,
}: WithInfiniteProps) => {
    // tslint:disable-next-line: no-any
    return React.forwardRef(function ListboxComponent(props: any, ref: any) {
        const { children, ...other } = props;

        // Children from Autocomplete are the *visible* (filtered) items.
        // Deriving itemCount from visibleItems (not the full items array) prevents
        // InfiniteLoader from firing loadNextPage solely because client-side
        // filtering reduced the visible count.
        const visibleItems = React.Children.toArray(children);

        // Read fresh values from refs on every render — no re-mount needed.
        const hasNextPage = hasNextPageRef.current;
        const isNextPageLoading = isNextPageLoadingRef.current;
        const itemSize = itemSizeRef.current;
        const height = heightRef.current;
        const reverseRender = reverseRenderRef.current;

        const itemCount = hasNextPage ? visibleItems.length + 1 : visibleItems.length;
        // tslint:disable-next-line: no-empty
        const loadMoreItems = isNextPageLoading ? () => {} : loadNextPage;
        // tslint:disable-next-line: no-any
        const isItemLoaded = (index: any) => !hasNextPage || index < visibleItems.length;

        const outerElementType = React.useMemo(() => {
            // tslint:disable-next-line: no-any
            return React.forwardRef((props2: any, ref2: any) => (
                <div ref={ref}>
                    <div ref={ref2} {...props2} {...other} />
                </div>
            ));
        // tslint:disable-next-line: align
        }, []); // eslint-disable-line react-hooks/exhaustive-deps

        const renderRow = ({ data, index, style }: ListChildComponentProps) => {
            // Sentinel row — shown while the next page is loading.
            if (!data || index >= data.length) {
                return (
                    <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                    </div>
                );
            }

            const individualData = reverseRender
                ? data[(data.length - 1) - index]
                : data[index];

            return React.cloneElement(individualData as React.ReactElement, { style: { ...style } });
        };

        return (
            <InfiniteLoader
                isItemLoaded={isItemLoaded}
                itemCount={itemCount}
                loadMoreItems={loadMoreItems}
                threshold={5}
            >
                {({ onItemsRendered, ref: loaderRef }) => (
                    <FixedSizeList
                        className="List"
                        layout="vertical"
                        style={{ padding: 0, maxWidth: '100%' }}
                        onScroll={(scroll: ListOnScrollProps) => {
                            scrollOffset = scroll.scrollOffset;
                        }}
                        initialScrollOffset={scrollOffset}
                        itemData={visibleItems}
                        height={height ?? 250}
                        width="100%"
                        outerElementType={outerElementType}
                        innerElementType={List}
                        itemSize={itemSize}
                        itemCount={itemCount}
                        onItemsRendered={onItemsRendered}
                        ref={loaderRef}
                    >
                        {renderRow}
                    </FixedSizeList>
                )}
            </InfiniteLoader>
        );
    });
};

// ---------------------------------------------------------------------------
// Public interfaces
// ---------------------------------------------------------------------------

// tslint:disable-next-line: no-any
export interface AutocompleteStandardV2Props<T> extends Omit<AutocompleteProps<T, any, any, any>, 'options' | 'renderInput'> {
    /** Legacy alias for `multiple` — kept for backwards compatibility. */
    isMultiple?: boolean;
    name: string;
    options?: T[];
    // tslint:disable-next-line: no-any
    renderInput?: (params: any) => React.ReactNode;
    // tslint:disable-next-line: no-any
    value?: T | any;
    // tslint:disable-next-line: no-any
    onSelection?: (value: IAutoCompleteItem | IAutoCompleteItem[] | any, name: string) => void;
    // tslint:disable-next-line: no-any
    onClose?: (event: React.ChangeEvent<{}>) => void;
    onOpen?: (event: React.ChangeEvent<{}>) => void;
}

interface InfiniteV2Props {
    hasNextPage: boolean;
    isNextPageLoading: boolean;
    // tslint:disable-next-line: no-any
    items: any;
    // tslint:disable-next-line: no-any
    loadNextPage: any;
    itemSize: number;
    isLoading?: boolean;
    height?: number;
    reverseRender?: boolean;
}

interface InfiniteAutocompleteV2Props<T> extends InfiniteV2Props, AutocompleteStandardV2Props<T> {
    disabledFilterOptions?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// tslint:disable-next-line: no-any
export const InfiniteAutocompleteV2: React.FC<InfiniteAutocompleteV2Props<any>> = props => {

    const {
        hasNextPage, isNextPageLoading, items, loadNextPage,
        itemSize, isLoading, height, reverseRender,
        isMultiple, onSelection, disabledFilterOptions,
        multiple, value,
        onChange,
        ...autocompleteProps
    } = props;

    // ------------------------------------------------------------------
    // Ref mirrors — keep listbox in sync with latest prop values without
    // needing to recreate the component type on every render.
    // ------------------------------------------------------------------
    const hasNextPageRef = useRef(hasNextPage);
    const isNextPageLoadingRef = useRef(isNextPageLoading);
    const itemSizeRef = useRef(itemSize);
    const heightRef = useRef(height);
    const reverseRenderRef = useRef(reverseRender);

    hasNextPageRef.current = hasNextPage;
    isNextPageLoadingRef.current = isNextPageLoading;
    itemSizeRef.current = itemSize;
    heightRef.current = height;
    reverseRenderRef.current = reverseRender;

    // Stable loadNextPage callback — the ref is updated every render so the
    // stable function always delegates to the latest version from props.
    const loadNextPageRef = useRef(loadNextPage);
    loadNextPageRef.current = loadNextPage;
    const stableLoadNextPage = useCallback(() => loadNextPageRef.current(), []);

    // Stable component type — created once so React never unmounts the
    // listbox mid-session, which would reset the scroll position to the top.
    // tslint:disable-next-line: no-any
    const ListboxComponent: any = useMemo(() => withInfinite({
        hasNextPageRef,
        isNextPageLoadingRef,
        loadNextPage: stableLoadNextPage,
        itemSizeRef,
        heightRef,
        reverseRenderRef,
    }), []); // eslint-disable-line react-hooks/exhaustive-deps

    // ------------------------------------------------------------------
    // Multiple / single mode
    // ------------------------------------------------------------------
    // Accept both `multiple` (MUI standard prop) and legacy `isMultiple`.
    // `multiple` takes precedence when both are provided.
    const isMultipleMode = multiple ?? isMultiple ?? false;

    // Normalise value so multiple mode always receives an array — prevents
    // Autocomplete controlled-value warnings when the parent passes undefined.
    const normalizedValue = isMultipleMode
        ? (Array.isArray(value) ? value : [])
        : value;

    // ------------------------------------------------------------------
    // Handlers
    // ------------------------------------------------------------------
    // tslint:disable-next-line: no-any
    const handleChange = (_event: React.ChangeEvent<{}>, newValue: any, reason: any, details?: any) => {
        if (onSelection) {
            onSelection(newValue, props.name);
        }
        if (onChange) {
            onChange(_event, newValue, reason, details);
        }
    };

    const handleClose = (event: React.ChangeEvent<{}>) => {
        if (props.onClose) {
            props.onClose(event);
        }
    };

    const handleOpen = (event: React.ChangeEvent<{}>) => {
        scrollOffset = 0;
        if (props.onOpen) {
            props.onOpen(event);
        }
    };

    /*
        Why matchSorter?
        Consider "Amanda Louise Heather" — the default Autocomplete filter only
        matches contiguous substrings ("Amanda Louise" or "Louise Heather"), so
        "Amanda Heather" would be missed.  matchSorter matches any term anywhere
        in the label, and splitting on spaces lets each word filter independently.
    */
    // tslint:disable-next-line: no-any
    const filterOptions = (options: any, filterOptionsState: FilterOptionsState<any>) => {
        if (disabledFilterOptions) {
            return items;
        }

        const input = filterOptionsState?.inputValue ?? '';

        if (input.length > 0) {
            const terms = input.split(' ');
            if (!terms) { return null; }
            // reduceRight: sorting is driven by score for the *first* entered word.
            return terms.reduceRight(
                // tslint:disable-next-line: no-any
                (results: any, term: string) => matchSorter(results, term, { keys: ['label'] }),
                options
            );
        }

        return items;
    };

    // ------------------------------------------------------------------
    // Render — single unified Autocomplete; no branching needed.
    // ------------------------------------------------------------------
    return (
        <Autocomplete
            {...autocompleteProps as any}
            multiple={isMultipleMode}
            value={normalizedValue}
            ListboxComponent={ListboxComponent}
            options={items}
            filterOptions={filterOptions}
            onChange={handleChange}
            onClose={handleClose}
            onOpen={handleOpen}
            loading={isLoading}
        />
    );
};
